import { useState, useEffect, useRef, useCallback } from "react"
import { useParams, useLocation } from "react-router-dom"
import Modal from "shared/modal"
import { SearchBar } from "shared"
import { SmartButtonStub } from "ui/smart-buttons"
import CheckInModal from "./CheckInModal"
import cable from 'channels/cable'

const POLL_INTERVAL = 4000

const CheckIn = () => {
  const { kioskId } = useParams()
  const location = useLocation()
  const query = new URLSearchParams(location.search)
  const checkinMode = query.get("checkinmode")

  const searchBarRef = useRef(null)

  const [state, setState] = useState({
    loading: false,
    memberId: "",
    modalOpened: false,
    person: null,
    events: [],
    conditionalRoleCheckins: [],
    checkinMode: "",
    error: "",
    successMessage: "",
    confirmationEmail: "",
    bannerUrl: null,
    placeId: null,
    placeCheckinId: null,
  })

  const resetSearchBar = () => {
    if (searchBarRef.current) searchBarRef.current.clear()
    setState((prev) => prev.memberId ? { ...prev, memberId: "" } : prev)
  }

  const autoCloseModal = () => {
    setTimeout(() => {
      setState((prev) => ({ ...prev, modalOpened: false }))
      resetSearchBar()
    }, 3000)
  }

  useEffect(() => {
    const fetchBanner = async () => {
      try {
        const res = await Backend.get("/users/kiosk_home_feed_banner")
        if (res?.banner_url) {
          setState((prev) => ({ ...prev, bannerUrl: res.banner_url }))
        }
      } catch (err) {
        console.error("Failed to fetch banner:", err)
      }
    }
    fetchBanner()
  }, [])

  useEffect(() => {
    const subscription = cable.subscriptions.create(
      { channel: "KioskChannel", id: kioskId },
      {
        connected: () => console.log("✅ Connected"),
        disconnected: () => console.log("❌ Disconnected"),
        received: (data) => {
          console.log('received', data)

          const params = new URLSearchParams(location.search)
          const checkinModeFromUrl = params.get("checkinmode")

          if (data.type == 'place_checkin' && data.kiosk_id == kioskId) {

            let newState = {
              person: data.user,
              placeId: data.place_checkin.place_id,
              placeCheckinId: data.place_checkin.id,
              modalOpened: true,
              error: "",
              conditionalRoleCheckins: [],
              successMessage: ""
            }

            if (checkinModeFromUrl == "automatic_events") {
              setState((prev) => ({
                ...prev,
                ...newState,
                checkinMode: "events",
                events: data.reservedEvents || []
              }))
            }
            else if (data.place_checkin.status == "confirmed") {
              setState((prev) => ({
                ...prev,
                ...newState,
                checkinMode: "automatic",
                successMessage: "Your Check In Was Successful!",
              }))
              autoCloseModal()
            }
            else if (data.place_checkin.status == "pending") {
              setState((prev) => ({
                ...prev,
                ...newState,
                checkinMode: "manual",
                conditionalRoleCheckins: data.conditional_role_checkins || []
              }))
            }
          }
        }
      }
    );

    return () => {
      console.log("Kiosk component unmounting, unsubscribing…");
      subscription.unsubscribe();
    };
  }, [location.pathname]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Enter" && state.memberId.trim()) {
        e.preventDefault()
        memberLookup()
      }
    }
    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [state.memberId])

  useEffect(() => {
    return
    if (!kioskId) return

    const interval = setInterval(async () => {
      if (state.modalOpened) return
      try {
        const res = await Backend.get(`/users/check_pending_checkins_place`, {
          kiosk_id: kioskId,
          checkin_mode: checkinMode,
        })

        if (checkinMode === "manual" && res?.user && res?.source !== "web") {
          setState((prev) => ({
            ...prev,
            person: res.user,
            conditionalRoleCheckins: res.conditional_role_checkins || [],
            checkinMode: "manual",
            placeId: res.place_id,
            placeCheckinId: res.place_checkin_id,
            modalOpened: true,
            error: "",
            successMessage: "",
          }))
        }

        if (checkinMode === "automatic" && res?.place_checkin_exist && res?.source !== "web") {
          setState((prev) => ({
            ...prev,
            person: res.user,
            conditionalRoleCheckins: [],
            checkinMode: "automatic",
            placeId: res.place_id,
            placeCheckinId: res.place_checkin_id,
            modalOpened: true,
            error: "",
            successMessage: "Your Check In Was Successful!",
          }))
          autoCloseModal()
        }
      } catch (err) {
        console.error("Polling error", err)
      }
    }, POLL_INTERVAL)

    return () => clearInterval(interval)
  }, [kioskId, state.modalOpened, checkinMode])

  /** Lookup Member */
  const memberLookup = useCallback(async () => {
    if (!state.memberId.trim()) return
    setState((prev) => ({ ...prev, loading: true, error: "", successMessage: "" }))

    try {
      const res = await Backend.get(`/users/find_member_id`, {
        member_id: state.memberId,
        kiosk_id: kioskId,
      })

      const { user, check_in_type, place_id, reservedEvents, conditional_role_checkins, place_checkin_id } = res

      if (!user) {
        setState((prev) => ({ ...prev, loading: false, error: "Invalid Member Number" }))
        return
      }

      if (check_in_type === "events") {
          try {
          const response = await Backend.post(`/users/${user.id}/create_auto_checkin_place`, {
            place_checkin: { user_id: user.id, place_id, status: "confirmed", source: "web" },
          })

          if (response?.success) {
            setState((prev) => ({
              ...prev,
              loading: false,
              person: user,
              events: reservedEvents,
              checkinMode: "events",
              successMessage: "",
              modalOpened: true,
              placeId: response ? response.place_id : null,
              placeCheckinId: response ? response.place_checkin_id : null,
            }))
            // autoCloseModal()
          } else {
            setState((prev) => ({ ...prev, loading: false, error: "Automatic Check In Failed" }))
          }
        } catch (err) {
          console.error(err)
          setState((prev) => ({ ...prev, loading: false, error: "Error during automatic check-in" }))
        }
      } else if (check_in_type === "automatic") {
        try {
          const response = await Backend.post(`/users/${user.id}/create_auto_checkin_place`, {
            place_checkin: { user_id: user.id, place_id, status: "confirmed", source: "web" },
          })

          if (response?.success) {
            setState((prev) => ({
              ...prev,
              loading: false,
              person: user,
              successMessage: "Your Check In Was Successful!",
              modalOpened: true,
            }))
            autoCloseModal()
          } else {
            setState((prev) => ({ ...prev, loading: false, error: "Automatic Check In Failed" }))
          }
        } catch (err) {
          console.error(err)
          setState((prev) => ({ ...prev, loading: false, error: "Error during automatic check-in" }))
        }
      } else if (check_in_type === "manual") {
        setState((prev) => ({
          ...prev,
          loading: false,
          person: user,
          conditionalRoleCheckins: conditional_role_checkins,
          checkinMode: "manual",
          modalOpened: true,
          placeId: place_id,
        }))
      }
    } catch (err) {
      console.error(err)
      setState((prev) => ({ ...prev, loading: false, error: "Error looking up member" }))
    }
  }, [state.memberId, kioskId])

  /** Modal Close */
  const modalClosed = ({ success } = {}) => {
    setState((prev) => ({
      ...prev,
      modalOpened: false,
      successMessage: "",
      error: "",
      ...(success && {
        loading: false,
        memberId: "",
        events: [],
        conditionalRoleCheckins: [],
        placeCheckinId: null,
      }),
    }))
    resetSearchBar()
  }

  /** JSX */
  return (
    <div id="kiosk-check-in-form">
      <form>
        <div className="form-group text-center">
          {state.bannerUrl && (
            state.bannerUrl.endsWith('.mp4') || state.bannerUrl.endsWith('.webm') ? (
              <video
                className="hero-image show"
                src={state.bannerUrl}
                style={{ maxWidth: "100%", height: "auto", maxHeight: "600px" }}
                autoPlay={true}
                loop={true}
                muted={true}
                controls={false}
              />
            ) : (
              <img
                className="hero-image show"
                src={state.bannerUrl}
                style={{ maxWidth: "100%", height: "auto", maxHeight: "600px" }}
                alt="Home Feed Banner"
              />
            )
          )}

          <h1
            className="parks-header text-center fw-bold"
            style={{ fontSize: "2.5rem", lineHeight: "1.4", marginTop: "2rem" }}
          >
            Tap Mobile Device to Check In <br />
            or Enter Member Number
          </h1>

          <div className="kiosk-search-page mx-auto mb-3" style={{ width: "30%" }}>
            <SearchBar
              ref={searchBarRef}
              placeholder="Member ID"
              initialQuery=""
              onChange={(query) =>
                setState((prev) => ({ ...prev, memberId: query, error: "", successMessage: "" }))
              }
              onSubmit={() => {}}
            />
          </div>
        </div>

        {/* <div className="form-actions text-center">
          <SmartButtonStub
            disabled={!state.memberId.trim()}
            text="Lookup Account"
            buttonType="button"
            className="admin-purple inverted primary custom-font-size mb-1 mb-md-0 py-2 w-25"
            spinning={state.loading}
            click={memberLookup}
          />
        </div> */}

        {state.error && <div className="text-danger text-center mt-2">{state.error}</div>}
      </form>

      {state.modalOpened && (
        <Modal opened onClose={modalClosed} size="xl">
          <CheckInModal
            person={state.person}
            events={state.events}
            conditionalRoleCheckins={state.conditionalRoleCheckins}
            checkinMode={state.checkinMode}
            successMessage={state.successMessage}
            errorMessage={state.error}
            placeId={state.placeId}
            placeCheckinId={state.placeCheckinId}
            modalCloseCallback={modalClosed}
          />
        </Modal>
      )}
    </div>
  )
}

export default CheckIn
